/* v8 ignore next */

import {
    Button,
    Divider,
    Dropdown,
    DropdownItem,
    Switch,
    TextArea,
    Code,
    Heading,
} from "@amzn/storm-ui";
import { useToggle } from "@react-hookz/web";
import React from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { DimensionTemplateBuilder } from "../components/dimensions/DimensionTemplateBuilder";
import { DimensionExamples } from "../fixtures/dimensions";
import { useGetRequestParameters } from "../hooks/dimension/dimension.hooks";
import { dimensionHydrationStore } from "../store/dimension.hydration";
import { dimensionStatusStore } from "../store/dimension.status";
import { dimensionValueStore } from "../store/dimension.value";
import { getStateKeys } from "../store/zustand.helpers";
import { getHashCode } from "../utils/string.utils";

const SettingsDiv = styled("div")`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const defaultHydrationPayload = {};

export const DimensionsTestingContainer: React.FunctionComponent = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [dimensionsBlob, setDimensionsBlob] = React.useState<string>("");
    const [dimensionExample, setDimensionExample] = React.useState<string>(
        searchParams.get("dimensionExample") || "",
    );

    const [customDimensions, toggleCustomDimension] =
        useToggle(!dimensionExample);
    const [dimensionDebugging, toggleDimensionDebug] = useToggle();
    const [isDimensionHydration, toggleDimensionHydration] = useToggle();
    const [dimensionHydrationPayload, setDimensionsHydrationPayload] =
        React.useState<string>("");
    const [isAdvertiserScopedCheck, setIsAdvertiserScopedCheck] =
        React.useState<boolean>(true);

    const [dimensionsIds] = useStore(dimensionValueStore, (state) => [
        getStateKeys(state),
    ]);
    const currentRequestParameters = useGetRequestParameters(dimensionsIds);

    const [hydrationStatus, hydrationErrorMessages, clearHydrationState] =
        useStore(dimensionHydrationStore, (state) => [
            state.dimensionHydrationStatus,
            state.errorMessages,
            state.clearAllState,
        ]);

    const dimensionTesting = React.useMemo(() => {
        // clear exisiting data
        dimensionStatusStore.getState().clearStatus();
        dimensionValueStore.getState().clearAllValues();
        clearHydrationState();
        if (dimensionsBlob.length > 0) {
            try {
                return {
                    id: "custom",
                    dimensions: JSON.parse(dimensionsBlob),
                };
            } catch {
                console.error("failed to parse");
            }
        }
        const selectedDimension = dimensionExample;
        const dimensionTemplate = DimensionExamples.find(
            (e) => e.id === selectedDimension,
        );
        return dimensionTemplate;
    }, [dimensionsBlob, searchParams, dimensionExample]);

    const [checkAllStatusSuccess, getAllStatus, clearStatusState] = useStore(
        dimensionStatusStore,
        (state) => [
            state.checkAllStatusSuccess,
            state.getAllStatus,
            state.clearStatus,
        ],
    );

    const allValid = checkAllStatusSuccess();
    const statuses = getAllStatus();

    const resetAllStores = () => {
        clearHydrationState();
        clearStatusState();
        dimensionValueStore.getState().clearAllValues();
    };

    return (
        <div style={{ margin: 50 }}>
            <Heading renderAs="h3">Dimensions Testing</Heading>
            <Divider />
            <Heading renderAs="h3">Settings</Heading>
            <SettingsDiv>
                <Switch
                    id="advertiserScoped"
                    label="automatic isAdvertiserScoped check"
                    toggled={isAdvertiserScopedCheck}
                    onChange={(e) => setIsAdvertiserScopedCheck(e)}
                />
            </SettingsDiv>
            <Divider />
            <Heading renderAs="h3">Dimension Configuration</Heading>
            <SettingsDiv>
                <Switch
                    id="toggleCustomDimension"
                    label="edit dimension manually"
                    toggled={customDimensions}
                    onChange={toggleCustomDimension}
                />
                <Switch
                    id="toggleDimensionDebug"
                    label="display dimension debug information"
                    toggled={dimensionDebugging}
                    onChange={toggleDimensionDebug}
                />
                <Dropdown
                    onChange={(e) => {
                        resetAllStores();
                        setDimensionExample(e);
                        toggleCustomDimension(false);
                        setSearchParams({ dimensionExample: e });
                    }}
                    selectedValue={dimensionExample}
                    label="Select Example"
                >
                    {DimensionExamples.map((e) => (
                        <DropdownItem key={e.id} value={e.id}>
                            {e.name}
                        </DropdownItem>
                    ))}
                </Dropdown>
            </SettingsDiv>
            <Divider />
            <Switch
                id="dimensionHydrationSwitch"
                label="Toggle custom Dimension Hydration Payload"
                toggled={isDimensionHydration}
                onChange={toggleDimensionHydration}
            />
            {isDimensionHydration && (
                <>
                    <br />
                    <Button
                        onClick={() => {
                            dimensionHydrationStore.getState().clearAllState();
                        }}
                    >
                        Reset Hydration
                    </Button>
                    <br />
                    <TextArea
                        id="dimensions-hydration"
                        label="Dimension Hydration"
                        fullWidth
                        value={dimensionHydrationPayload}
                        onChange={(e) => {
                            setDimensionsHydrationPayload(e.target.value);
                        }}
                        style={{ height: 300 }}
                    />
                    <Divider />
                </>
            )}
            <Divider />
            {customDimensions && (
                <>
                    <TextArea
                        id="dimensions-test"
                        label="dimensions"
                        fullWidth
                        value={dimensionsBlob}
                        onChange={(e) => setDimensionsBlob(e.target.value)}
                        style={{ height: 600 }}
                    />
                    <Divider />
                </>
            )}
            {!customDimensions && dimensionTesting?.dimensions && (
                <>
                    <TextArea
                        id="dimensions-example-"
                        label="dimensions"
                        fullWidth
                        value={JSON.stringify(
                            dimensionTesting.dimensions,
                            null,
                            4,
                        )}
                        disabled
                        style={{ height: 800 }}
                    />
                    <Divider />
                </>
            )}
            <Heading renderAs="h3">Dimensions Built Output</Heading>
            {dimensionTesting?.dimensions && (
                <DimensionTemplateBuilder
                    key={`${dimensionTesting.id}_${getHashCode(dimensionHydrationPayload)}`}
                    dimensionSections={dimensionTesting.dimensions}
                    skipHasAdvertiser={!isAdvertiserScopedCheck}
                    enableDimensionsDebug={dimensionDebugging}
                    hydrationPayload={(() => {
                        // use default hydration payload for hot reloading
                        if (
                            !isDimensionHydration &&
                            Object.keys(defaultHydrationPayload).length > 0
                        ) {
                            return defaultHydrationPayload;
                        }

                        if (isDimensionHydration) {
                            try {
                                return JSON.parse(dimensionHydrationPayload);
                            } catch {
                                return {};
                            }
                        }
                        return undefined;
                    })()}
                />
            )}
            <Divider />
            <Heading renderAs="h3">Request Parameter</Heading>
            <Code renderAs="pre">
                {JSON.stringify(currentRequestParameters, null, 4)}
            </Code>
            <Divider />
            <Heading renderAs="h3">Dimension Status</Heading>
            <Code renderAs="pre">
                Dimension Template Form valid: {JSON.stringify(allValid)}
            </Code>
            <Code renderAs="pre">{JSON.stringify(statuses, null, 4)}</Code>
            <Heading renderAs="h3">Dimension Hydration Status</Heading>
            <Code renderAs="pre">
                {JSON.stringify(hydrationStatus, null, 4)}
            </Code>
            <Heading renderAs="h3">Dimension Hydration Errors</Heading>
            <Code renderAs="pre">
                {JSON.stringify(hydrationErrorMessages, null, 4)}
            </Code>
        </div>
    );
};
